import { Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components";
import FormHeader from "../../../components/form/FormHeader";
import EventDetailsView from "../components/EventDetailsView";
import { useGetDetailEvent } from "../hooks";

const toTitleCase = (value: string) =>
  value
    .split(/[ _-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatTimestamp = (value?: string) => {
  if (!value) return "Not available";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

type EventDetailsPageProps = {
  basePath?: string;
};

export default function EventDetailsPage({ basePath = "/event" }: EventDetailsPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { event, loading: isLoadingEvent, error: eventError } = useGetDetailEvent(id);

  if (isLoadingEvent && !event) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          <p className="text-sm text-gray-600 dark:text-gray-300">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (eventError && !event) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{eventError}</p>
          <Button
            onClick={() => navigate(basePath)}
            type="button"
          >
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  if (!event) return null;

  const status = event.event_status;
  const canEdit = event.event_status !== "completed";

  return (
    <>
      <FormHeader
        onBack={() => navigate(basePath)}
        title={event.title}
        subtitle={`${toTitleCase(status)} · Created ${formatTimestamp(event.created_at)} · Last updated ${formatTimestamp(
          event.updated_at,
        )}`}
        backLabel={t("common.back")}
        actions={
          canEdit ? (
            <Button
              type="button"
              onClick={() => navigate(`${basePath}/update/${event.id}`)}
              className=" text-white"
            >
              <Pencil aria-hidden="true" />
              Edit event
            </Button>
          ) : undefined
        }
      />
      <EventDetailsView event={event} />
    </>
  );
}
